"use client"

import { changeShippingMethod } from "@/actions/selection/client/change-shipping-method"
import IconAusParcelPost from "@/app/(site)/_components/Atoms/Icons/IconAusParcelPost"
import IconDhl from "@/app/(site)/_components/Atoms/Icons/IconDhl"
import IconDpdPost from "@/app/(site)/_components/Atoms/Icons/IconDpdPost"
import IconUps from "@/app/(site)/_components/Atoms/Icons/IconUps"
import Radio from "@/app/(site)/_components/Atoms/Inputs/radio"
import ButtonElement from "@/app/(site)/_components/GCMolecules/ButtonElement"
import RichTextElement from "@/app/(site)/_components/GCMolecules/RichTextElement"
import { useSWRBasketSessionState } from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { type CheckoutStep } from "@/app/(site)/contexts/ecommerce/types"
import logger from "@/lib/logger/logger-init"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { applyFreeShippingToCheapestMethod } from "@/lib/utils/checkout/shippingUtils"
import { parseShippingMethodName } from "@/lib/utils/checkout/voucherUtils"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { getTypographyStyles } from "@/lib/utils/typography/getTypographyStyles"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { type BasketSessionState } from "@/types/centra/basket-session-state"
import { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import type { PortableTextBlock } from "sanity"

const carrierIconClassName = "block h-12 w-auto max-h-12"

export const ShippingMethodNameWithIcon = ({ methodName }: { methodName: string }) => (
    <span className="inline-flex items-center gap-4">
        <span className="translate-y-2 Trade_Gothic_Text_Bold_14_150_0_700">
            {methodName}
        </span>
        <CarrierIcon carrierName={methodName} />
    </span>
)

export const CarrierIcon = ({ carrierName }: { carrierName: string }) => {
    const lowerName = carrierName.toLowerCase()

    const icon = lowerName.includes("ups") ? (
        <IconUps className={carrierIconClassName} />
    ) : lowerName.includes("dpd") ? (
        <IconDpdPost className={carrierIconClassName} />
    ) : lowerName.includes("dhl") ? (
        <IconDhl className={carrierIconClassName} />
    ) : (
        <IconAusParcelPost className={carrierIconClassName} />
    )

    return (
        <span
            aria-hidden
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center"
        >
            {icon}
        </span>
    )
}

interface ShippingMethod {
    shippingMethod: string
    name: string
    description?: string
    price: string
    priceAsNumber: number
    selected: boolean
    freeShipping?: boolean
    deliveryTime?: string
}

interface ShippingMethodsProps {
    className?: string
    langCode: string
    setStep: (step: CheckoutStep) => void
    paymentPrivacyNotice?: PortableTextBlock[]
}

export const ShippingMethods = ({
    setStep,
    className = "",
    langCode,
    paymentPrivacyNotice
}: ShippingMethodsProps) => {
    const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null)
    const [updating, setUpdating] = useState(false)

    // Fetch selection data using SWR
    const { selection, shippingMethods, mutate, isLoadingSelection } =
        useSWRBasketSessionState()
    const currentShippingMethod = selection?.shippingMethod

    const siteContext = useSiteContext()
    const hasTrackedShippingPage = useRef<boolean>(false)
    const lastTrackedShippingMethod = useRef<string | null>(null)

    const vouchers = selection?.discounts?.vouchers || []

    const updatedShippingMethods = applyFreeShippingToCheapestMethod(
        shippingMethods,
        vouchers,
        langCode
    )

    const handleContinue = () => {
        setStep(3)
    }

    const handleMethodSelect = async (method: ShippingMethod) => {
        if (method.shippingMethod === selectedMethodId) return

        setUpdating(true)
        setSelectedMethodId(method.shippingMethod)

        try {
            const formData = new FormData()
            formData.append("lineId", method.shippingMethod)

            const result = await changeShippingMethod(formData)

            if (result.success) {
                mutate()
                    .then((data?: BasketSessionState) => {
                        const selection =
                            data?.status === "active"
                                ? data.checkoutApiSelection.selection
                                : undefined
                        // Only track GTM event if shipping method actually changed
                        if (
                            lastTrackedShippingMethod.current !== method.shippingMethod &&
                            selection
                        ) {
                            import("@/lib/utils/tracking/gtmEvents").then(
                                ({ addShippingInfoEvent }) => {
                                    addShippingInfoEvent(selection, siteContext)
                                }
                            )
                            lastTrackedShippingMethod.current = method.shippingMethod
                        }
                    })
                    .catch(error => {
                        logger.error("Error updating shipping method:", error)
                    })
                    .finally(() => {
                        setUpdating(false)
                    })
            } else {
                // Revert selection on failure
                setSelectedMethodId(currentShippingMethod ?? null)
                throw new Error(
                    result.error?.message || "Failed to update shipping method"
                )
            }
        } catch (error) {
            console.error("Error updating shipping method:", error)
            await mutate()
            toast.error(
                t(langCode, "checkout.shipping.shipping_method_failed") ||
                    "Failed to update shipping method"
            )
        } finally {
            setUpdating(false)
        }
    }

    useEffect(() => {
        if (selection) {
            // Tracking
            if (!hasTrackedShippingPage.current) {
                const voucherCode = selection?.discounts?.vouchers
                    ?.map((voucher: any) => voucher.voucher)
                    .filter(
                        (voucherCode: string) =>
                            voucherCode !== FREE_SHIPPING_VOUCHER_CODE
                    )
                    .join(", ")
                import("@/lib/utils/tracking/gtmEvents").then(({ shippingPageEvent }) => {
                    shippingPageEvent(selection, siteContext, voucherCode)
                })
                hasTrackedShippingPage.current = true
            }

            // Select the free shipping method if available, otherwise select the first one
            if (
                updatedShippingMethods.length > 0 &&
                !selectedMethodId &&
                siteContext.site_country &&
                siteContext.site_language
            ) {
                // Find the free shipping method first
                const freeShippingMethod = updatedShippingMethods.find(
                    (method: any) => method.isFree === true
                ) as ShippingMethod | undefined

                // Select free shipping if available, otherwise select the first method
                const methodToSelect = freeShippingMethod || updatedShippingMethods[0]
                handleMethodSelect(methodToSelect as ShippingMethod)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selection), siteContext.site_country, siteContext.site_language])

    return (
        <form
            onSubmit={e => {
                e.preventDefault()
                e.stopPropagation()
                handleContinue()
            }}
            className={`flex flex-col gap-20 ${className}`}
        >
            <div className="rounded-lg border border-col-border-neutral">
                {updatedShippingMethods.map((method: any, index: number) => {
                    const isSelected = method.shippingMethod === selectedMethodId
                    const isFree = method.priceAsNumber === 0

                    const { name: methodName, deliveryTime } = parseShippingMethodName(
                        method.name,
                        langCode
                    )

                    return (
                        <div
                            key={`${method.shippingMethod}-${index}`}
                            className={clsx(
                                "flex border-b border-col-border-neutral px-12 pb-12 pt-16 transition-colors duration-300 hover:bg-col-bg-calm",
                                isSelected ? "bg-col-bg-calm" : "",
                                index === 0 ? "rounded-t-lg" : "",
                                index === updatedShippingMethods.length - 1
                                    ? "rounded-b-lg border-b-0"
                                    : ""
                            )}
                        >
                            <Radio
                                value={method.shippingMethod}
                                name={methodName}
                                label={
                                    <div className="flex w-full flex-col gap-2">
                                        <div className="flex justify-between">
                                            <ShippingMethodNameWithIcon
                                                methodName={methodName}
                                            />
                                            <span
                                                className={clsx(
                                                    isFree && "text-col-text-primary",
                                                    "ml-auto Trim_Mono_POC_Text_14_130_-5_400_uppercase"
                                                )}
                                            >
                                                {isFree
                                                    ? t(
                                                          langCode,
                                                          "checkout.shipping.free"
                                                      )
                                                    : method.price}
                                            </span>
                                        </div>
                                        {deliveryTime && (
                                            <span className="Trade_Gothic_Text_14_150_0_300">
                                                {deliveryTime}
                                            </span>
                                        )}
                                    </div>
                                }
                                checked={isSelected}
                                onChange={() => {
                                    handleMethodSelect(method)
                                }}
                                disabled={updating}
                                containerClassName="flex w-full"
                                inseta11y={{
                                    before: "before:absolute before:inset-0 inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']"
                                }}
                            />
                        </div>
                    )
                })}
            </div>
            {paymentPrivacyNotice && (
                <RichTextElement
                    richTextGroup={paymentPrivacyNotice}
                    overrideFont={getTypographyStyles(".Trade_Gothic_Text_14_150_0_300")}
                />
            )}
            <ButtonElement
                onClick={handleContinue}
                disabled={updating || isLoadingSelection}
                className="w-full justify-center"
                text={t(langCode, "checkout.continue_to_payment")}
                theme="primary-black"
                size={{ desktop: "large", mobile: "small" }}
                type="submit"
            />
        </form>
    )
}
